import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

type QueryResult = {
	data?: { authorizationUrl: string } | undefined;
	error?: unknown;
};

type UseLazyQueryHook = () => [
	(args: {
		companyDomainId: string;
		redirectUrl: string;
		external?: boolean;
	}) => Promise<QueryResult>,
	{ isLoading: boolean },
	...unknown[],
];

export const useOAuthAuthorizationUrl = ({
	useLazyQuery,
	externalCallbackPath,
	urlSystemIdentifier,
	errorMessageKey,
	companyDomainId,
	extraRedirectParams,
}: {
	useLazyQuery: UseLazyQueryHook;
	externalCallbackPath: string;
	urlSystemIdentifier: string;
	errorMessageKey: string;
	companyDomainId: string;
	extraRedirectParams?: Record<string, string> | undefined;
}) => {
	const [trigger, { isLoading }] = useLazyQuery();
	const { t } = useTranslation();
	const isProcessingRef = useRef(false);

	return {
		isLoading,
		onRequestAuthorizationUrl: useCallback(
			async (external?: boolean) => {
				if (isProcessingRef.current) {
					return;
				}

				let url: URL;
				if (external) {
					url = new URL(externalCallbackPath, location.origin);
				} else {
					url = new URL(location.pathname, location.origin);
					url.searchParams.set("system", urlSystemIdentifier);
					for (const [key, value] of Object.entries(
						extraRedirectParams ?? {},
					)) {
						url.searchParams.set(key, value);
					}
				}

				const result = await trigger({
					companyDomainId,
					redirectUrl: url.toString(),
					external: Boolean(external),
				});

				isProcessingRef.current = false;

				if ("error" in result) {
					showErrorNotification({ message: t(errorMessageKey) });
					return null;
				}
				return result.data!.authorizationUrl;
			},
			[
				trigger,
				companyDomainId,
				externalCallbackPath,
				urlSystemIdentifier,
				errorMessageKey,
				extraRedirectParams,
				t,
			],
		),
	};
};
